import ApiExtension from './api-extension'
import { Farm, Field, Project as ProjectType } from '../types/project'

type FieldWithoutFarm = Omit<Field, 'farm'>

export type ProjectOptions = {
  farms: Farm[]
  fields: FieldWithoutFarm[]
  crops: string[]
}

/* eslint-disable camelcase */
type ProjectUpdate = {
  survey_date: string
  metadata: Record<string, unknown>
  farm_id: number
  fields: string
}
/* eslint-enable camelcase */

export default class Project extends ApiExtension {
  getOptions(projectId: number): Promise<ProjectOptions> {
    return this.fetch('options_project', { id: projectId })
  }

  update(projectId: number, update: Partial<ProjectUpdate>): Promise<void> {
    return this.fetch(
      'project',
      { id: projectId },
      { method: 'PUT', body: { project: update } }
    )
  }

  fetchZonalStatistics(projectId: number) {
    return this.fetch('zonal_statistics_project', { id: projectId })
  }

  putZonalStatistics(projectId: number, zonalStatistics: string) {
    return this.fetch(
      'zonal_statistics_project',
      { id: projectId },
      { method: 'PUT', body: { zonal_statistics: zonalStatistics } }
    )
  }

  touch(projectId: number): Promise<void> {
    return this.fetch('touch_project', { id: projectId }, { method: 'PUT' })
  }

  delete(projectId: number): Promise<void> {
    return this.fetch('project', { id: projectId }, { method: 'DELETE' })
  }

  clearTileCache(
    projectId: number,
    resource: string | null = null
  ): Promise<void> {
    return this.fetch(
      'tile_cache_project',
      { id: projectId },
      { method: 'DELETE', body: { resource } }
    )
  }
}

export type PropertiesUpdate = {
  surveyDate: string
  crop: string | null
  altitude: number | null
  farmId?: number
  fieldName?: string
}

/**
 * Give a set of updated project properties, returns a project update suitable
 * for sending to the back end to persist the updates.
 *
 * @param project the project state for the project to update
 * @param propertiesUpdate the updated properties
 * @returns an update object suitable for sending to the back end for updating the project
 */
export function createProjectUpdate(
  project: ProjectType,
  propertiesUpdate: Partial<PropertiesUpdate>
): Partial<ProjectUpdate> {
  const updateCopy: Partial<PropertiesUpdate> = { ...propertiesUpdate }
  const projectProperties: Partial<Record<
    keyof PropertiesUpdate,
    keyof ProjectUpdate
  >> = {
    surveyDate: 'survey_date',
    farmId: 'farm_id',
    fieldName: 'fields',
  }
  const metadataProperties: Partial<Record<keyof PropertiesUpdate, string>> = {
    crop: 'crop',
    altitude: 'relative_altitude',
  }

  const update: Partial<ProjectUpdate> = {}

  for (const prop of Object.keys(projectProperties) as Array<
    keyof Partial<PropertiesUpdate>
  >) {
    if (updateCopy[prop] !== undefined) {
      const projectProperty = projectProperties[prop]
      if (projectProperty) {
        update[projectProperty] = updateCopy[prop] as any
        delete updateCopy[prop]
      }
    }
  }

  for (const prop of Object.keys(metadataProperties) as Partial<
    keyof PropertiesUpdate
  >[]) {
    if (updateCopy[prop] !== undefined) {
      const metadataProperty = metadataProperties[prop]
      if (metadataProperty) {
        if (!update.metadata) {
          update.metadata = { ...project.metadata.data }
        }

        ;(update.metadata as Record<string, unknown>)[
          metadataProperty
        ] = updateCopy[prop] as any
      }
      delete updateCopy[prop]
    }
  }

  if (Object.keys(updateCopy).length > 0) {
    throw new Error(
      `Properties update contains unmapped properties: ${Object.keys(
        updateCopy
      ).join(', ')}`
    )
  }

  return update
}
